import { useState, useEffect, useCallback, useRef } from 'react'

// ── useDebounce ───────────────────────────────────────────────────────────────
export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

// ── usePagination ─────────────────────────────────────────────────────────────
export interface UsePaginationOptions {
  initialPage?: number
  initialLimit?: number
}

export interface UsePaginationReturn {
  page: number
  limit: number
  setPage: (page: number) => void
  setLimit: (limit: number) => void
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
  reset: () => void
}

export function usePagination({
  initialPage = 1,
  initialLimit = 20,
}: UsePaginationOptions = {}): UsePaginationReturn {
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialLimit)

  const handleSetLimit = useCallback((newLimit: number) => {
    setLimit(newLimit)
    setPage(1) // reset page quand on change le nb par page
  }, [])

  return {
    page,
    limit,
    setPage,
    setLimit: handleSetLimit,
    nextPage: () => setPage((p) => p + 1),
    prevPage: () => setPage((p) => Math.max(1, p - 1)),
    goToPage: setPage,
    reset: () => { setPage(initialPage); setLimit(initialLimit) },
  }
}

// ── useLocalStorage ───────────────────────────────────────────────────────────
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  const setValue = useCallback(
    (value: T) => {
      try {
        setStoredValue(value)
        window.localStorage.setItem(key, JSON.stringify(value))
      } catch (err) {
        console.warn('[useLocalStorage] set error', err)
      }
    },
    [key],
  )

  return [storedValue, setValue]
}

// ── useMediaQuery ─────────────────────────────────────────────────────────────
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)

  useEffect(() => {
    const media = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [query])

  return matches
}

export const useIsMobile = () => useMediaQuery('(max-width: 768px)')
export const useIsTablet = () => useMediaQuery('(max-width: 1024px)')

// ── useConfirm ────────────────────────────────────────────────────────────────
export interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'default'
}

export interface UseConfirmReturn {
  isOpen: boolean
  options: ConfirmOptions | null
  confirm: (options: ConfirmOptions) => Promise<boolean>
  onConfirm: () => void
  onCancel: () => void
}

export function useConfirm(): UseConfirmReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolveRef = useRef<(value: boolean) => void>()

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts)
    setIsOpen(true)
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const onConfirm = useCallback(() => {
    setIsOpen(false)
    resolveRef.current?.(true)
  }, [])

  const onCancel = useCallback(() => {
    setIsOpen(false)
    resolveRef.current?.(false)
  }, [])

  return { isOpen, options, confirm, onConfirm, onCancel }
}

// ── useNotifications (placeholder — connecté à notification.store) ─────────────
export function useNotifications() {
  // TODO Phase 4 : Connecter au notification.store + Socket.io
  return {
    unreadCount: 0,
    notifications: [],
    markAsRead: (_id: string) => {},
    markAllAsRead: () => {},
  }
}
